import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { format, addDays, subDays, isBefore, isSameDay, startOfDay, eachDayOfInterval } from 'date-fns';
import { Calendar, Clock, User, Mail, MessageSquare, ChevronLeft, ChevronRight, Plus, X, CheckCircle, AlertCircle } from 'lucide-react';
import { apiGet, apiPost, convertLocalTimeToEpoch } from '../../utils/api';
import { Button, Card, Input, Select, Spinner, Alert, Badge } from '../design-system';

const PublicBooking = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [therapistInfo, setTherapistInfo] = useState(null);
  const [weekCache, setWeekCache] = useState({});
  const [weekEvents, setWeekEvents] = useState([]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // Get therapist ID from URL params
  const therapistId = window.location.pathname.split('/').pop();

  // Generate time options (24-hour format for easier epoch conversion)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push(timeString);
    }
  }

  // Helper function to get week range (3 days before + today + 3 days after)
  const getWeekRange = (date) => {
    const threeDaysBefore = subDays(date, 3);
    const threeDaysAfter = addDays(date, 3);
    
    return {
      start: threeDaysBefore,
      end: threeDaysAfter,
      days: eachDayOfInterval({ start: threeDaysBefore, end: threeDaysAfter })
    };
  };

  // Helper function to get week key for caching
  const getWeekKey = (date) => {
    const weekRange = getWeekRange(date);
    return format(weekRange.start, 'yyyy-MM-dd');
  };

  // Helper function to check if date is in the past
  const isPastDate = (date) => {
    return isBefore(date, startOfDay(new Date()));
  };

  // Helper function to check if time is in the past for today
  const isPastTime = (date, time) => {
    const now = new Date();
    const isToday = isSameDay(date, now);
    
    if (!isToday) return false;
    
    const [hours, minutes] = time.split(':');
    const selectedDateTime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      parseInt(hours),
      parseInt(minutes)
    );
    
    return isBefore(selectedDateTime, now);
  };

  const fetchTherapistInfo = async () => {
    try {
      const response = await apiGet(`/.netlify/functions/get-therapist-info?therapistId=${therapistId}`);
      setTherapistInfo(response.therapist);
    } catch (error) {
      console.error('Error fetching therapist info:', error);
    }
  };

  const fetchWeekData = async (date) => {
    try {
      setLoading(true);
      const weekRange = getWeekRange(date);
      const weekKey = getWeekKey(date);
      
      // Check cache first
      if (weekCache[weekKey]) {
        setWeekEvents(weekCache[weekKey]);
        return;
      }
      
      const weekStart = format(weekRange.start, 'yyyy-MM-dd');
      const weekEnd = format(weekRange.end, 'yyyy-MM-dd');
      
      const apiUrl = `/.netlify/functions/get-available-slots?weekStart=${weekStart}&weekEnd=${weekEnd}&therapistId=${therapistId}`;
      
      const response = await apiGet(apiUrl);
      
      // Cache the week data
      setWeekCache(prev => ({ ...prev, [weekKey]: response.weekEvents || [] }));
      setWeekEvents(response.weekEvents || []);
    } catch (error) {
      console.error('Error fetching week data:', error);
      toast.error('Failed to load availability data');
    } finally {
      setLoading(false);
    }
  };

  const generateSlotsForDay = (events, targetDate) => {
    // Don't filter by day boundaries again - use all week events
    // Convert all events to local time and group by local date
    
    // Convert all events to local time and find those that fall on the target date
    const relevantEvents = events.filter(event => {
      const eventStartDate = new Date(event.startEpoch * 1000);
      const eventEndDate = new Date(event.endEpoch * 1000);
      
      // Check if any part of this event falls on the target date in local time
      const targetDateStart = new Date(format(targetDate, 'yyyy-MM-dd') + 'T00:00:00');
      const targetDateEnd = new Date(format(targetDate, 'yyyy-MM-dd') + 'T23:59:59');
      
      return (eventStartDate < targetDateEnd && eventEndDate > targetDateStart);
    });



    // Separate availability windows and booked/blocked times
    const availabilityWindows = relevantEvents.filter(event => event.type === 'available');
    const bookedTimes = relevantEvents.filter(event => event.type === 'booked');
    const blockedTimes = relevantEvents.filter(event => event.type === 'blocked');

    // Generate 30-minute slots for the target date in local time
    const dayStart = new Date(format(targetDate, 'yyyy-MM-dd') + 'T00:00:00');
    const dayEnd = new Date(format(targetDate, 'yyyy-MM-dd') + 'T23:59:59');
    const dayStartEpoch = Math.floor(dayStart.getTime() / 1000);
    const dayEndEpoch = Math.floor(dayEnd.getTime() / 1000);



    // Generate 30-minute slots
    const slots = [];
    for (let epoch = dayStartEpoch; epoch < dayEndEpoch; epoch += 1800) {
      const slotEndEpoch = epoch + 1800;
      
      // Check if this slot is available
      const isAvailable = availabilityWindows.some(event => 
        epoch >= event.startEpoch && 
        slotEndEpoch <= event.endEpoch
      );

      const isBooked = bookedTimes.some(event =>
        epoch < event.endEpoch && 
        slotEndEpoch > event.startEpoch
      );

      const isBlocked = blockedTimes.some(event =>
        epoch < event.endEpoch && 
        slotEndEpoch > event.startEpoch
      );

      if (isAvailable && !isBooked && !isBlocked) {
        slots.push({
          startEpoch: epoch,
          endEpoch: slotEndEpoch
        });
      }
    }

    return slots;
  };

  const getAvailabilityCount = (date) => {
    const slots = generateSlotsForDay(weekEvents, date);
    return slots.length;
  };

  useEffect(() => {
    fetchTherapistInfo();
  }, []);

  useEffect(() => {
    fetchWeekData(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    // Generate slots for selected date when week events change
    if (weekEvents.length > 0) {
      const slots = generateSlotsForDay(weekEvents, selectedDate);
      setAvailableSlots(slots);
    }
  }, [weekEvents, selectedDate]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Validate time inputs
      if (data.startTime >= data.endTime) {
        toast.error('End time must be after start time');
        return;
      }
      
      // Validate duration
      if (!data.duration || data.duration < 15 || data.duration > 480) {
        toast.error('Duration must be between 15 minutes and 8 hours');
        return;
      }
      
      // Check if selected date/time is in the past
      if (isPastDate(selectedDate)) {
        toast.error('Cannot book appointments in the past');
        return;
      }
      
      if (isPastTime(selectedDate, data.startTime)) {
        toast.error('Cannot book appointments in the past');
        return;
      }
      
      const startEpoch = convertLocalTimeToEpoch(data.startTime, selectedDate);
      const endEpoch = convertLocalTimeToEpoch(data.endTime, selectedDate);

      // Validate time range
      if (startEpoch >= endEpoch) {
        toast.error('Invalid time range');
        return;
      }

      const bookingData = {
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: startEpoch, // Send epoch timestamp
        endTime: endEpoch, // Send epoch timestamp
        duration: data.duration,
        message: data.message || '',
        therapistId: therapistId
      };

      await apiPost('/.netlify/functions/book-slot', bookingData);
      toast.success('Booking confirmed successfully!');
      reset();
      setShowBookingForm(false);
      setSelectedSlot(null);
      
      // Refresh week data to show updated availability
      fetchWeekData(selectedDate);
    } catch (error) {
      console.error('Error booking slot:', error);
      toast.error(error.message || 'Failed to book slot');
    } finally {
      setLoading(false);
    }
  };

  const formatEpochToLocalTime = (epoch) => {
    const date = new Date(epoch * 1000); // Convert epoch seconds to milliseconds
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  };

  const formatTimeRange = (startEpoch, endEpoch) => {
    const startTime = formatEpochToLocalTime(startEpoch);
    const endTime = formatEpochToLocalTime(endEpoch);
    return `${startTime} - ${endTime}`;
  };

  const calculateDuration = (startEpoch, endEpoch) => {
    const durationMinutes = (endEpoch - startEpoch) / 60;
    return `${durationMinutes} minutes`;
  };

  const handleDateSelection = (date) => {
    if (isPastDate(date)) {
      return; // Don't allow selection of past dates
    }
    setSelectedDate(date);
  };

  const WeekCalendar = () => {
    const weekRange = getWeekRange(selectedDate);
    const today = new Date();
    
    return (
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {weekRange.days.map((day) => {
          const isPast = isPastDate(day);
          const isToday = isSameDay(day, today);
          const isSelected = isSameDay(day, selectedDate);
          
          return (
            <button
              key={day.toISOString()}
              onClick={() => handleDateSelection(day)}
              disabled={isPast}
              className={`p-2 md:p-3 rounded-lg border transition-colors ${
                isPast
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : isSelected
                  ? 'bg-primary-600 text-white border-primary-600'
                  : isToday
                  ? 'border-primary-300 bg-primary-50'
                  : 'hover:bg-gray-50 border-gray-200'
              }`}
            >
              <div className="text-xs md:text-sm font-medium">
                {format(day, 'EEE')}
              </div>
              <div className="text-base md:text-lg">
                {format(day, 'd')}
              </div>
              <div className="text-xs">
                {isPast ? 'Past' : `${getAvailabilityCount(day)} slots`}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Card variant="elevated" padding="large" className="mb-4 md:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Book Appointment</h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">Schedule your session with your therapist</p>
            </div>
            {therapistInfo && (
              <div className="text-left sm:text-right">
                <p className="text-sm font-medium text-gray-900">{therapistInfo.name}</p>
                <p className="text-sm text-gray-500">{therapistInfo.email}</p>
              </div>
            )}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Date Selection and Available Slots */}
          <div className="space-y-4 md:space-y-6">
            {/* 7-Day Calendar */}
            <Card variant="default" padding="large">
              <h2 className="text-base md:text-lg font-medium text-gray-900 mb-4">Select Date</h2>
              <WeekCalendar />
            </Card>

            {/* Available Slots */}
            <Card variant="default" padding="large">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
                <h2 className="text-base md:text-lg font-medium text-gray-900">
                  Available Slots for {format(selectedDate, 'MMMM d, yyyy')}
                </h2>
                <Button
                  onClick={() => setShowBookingForm(!showBookingForm)}
                  variant="primary"
                  size="small"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Book Custom Time
                </Button>
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <Spinner size="medium" className="mx-auto" />
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No available slots for this date</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedSlot(slot);
                        setShowBookingForm(true);
                      }}
                      className="p-3 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors duration-200 text-left"
                    >
                      <p className="font-medium text-gray-900">
                        {formatEpochToLocalTime(slot.startEpoch)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {calculateDuration(slot.startEpoch, slot.endEpoch)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Booking Form */}
          <Card variant="default" padding="large">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base md:text-lg font-medium text-gray-900">Book Appointment</h2>
              {showBookingForm && (
                <Button
                  onClick={() => {
                    setShowBookingForm(false);
                    setSelectedSlot(null);
                    reset();
                  }}
                  variant="ghost"
                  size="small"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {!showBookingForm ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Select a date and time to book your appointment</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Select
                      label="Start Time"
                      value={selectedSlot ? (() => { const d = new Date(selectedSlot.startEpoch * 1000); const hh = d.getHours().toString().padStart(2, '0'); const mm = d.getMinutes().toString().padStart(2, '0'); return `${hh}:${mm}`; })() : ''}
                      onChange={(e) => {
                        register('startTime', { required: 'Start time is required' }).onChange(e);
                      }}
                      options={[
                        { value: '', label: 'Select start time' },
                        ...timeOptions.map(time => {
                          const isPast = isPastTime(selectedDate, time);
                          return {
                            value: time,
                            label: `${time} ${isPast ? '(Past)' : ''}`
                          };
                        })
                      ]}
                      error={!!errors.startTime}
                      errorText={errors.startTime?.message}
                    />
                  </div>

                  <div>
                    <Select
                      label="End Time"
                      value={selectedSlot ? (() => { const d = new Date(selectedSlot.endEpoch * 1000); const hh = d.getHours().toString().padStart(2, '0'); const mm = d.getMinutes().toString().padStart(2, '0'); return `${hh}:${mm}`; })() : ''}
                      onChange={(e) => {
                        register('endTime', { required: 'End time is required' }).onChange(e);
                      }}
                      options={[
                        { value: '', label: 'Select end time' },
                        ...timeOptions.map(time => {
                          const isPast = isPastTime(selectedDate, time);
                          return {
                            value: time,
                            label: `${time} ${isPast ? '(Past)' : ''}`
                          };
                        })
                      ]}
                      error={!!errors.endTime}
                      errorText={errors.endTime?.message}
                    />
                  </div>
                </div>

                <div>
                  <Input
                    type="number"
                    label="Duration (minutes)"
                    placeholder="30"
                    {...register('duration', { 
                      required: 'Duration is required',
                      min: { value: 15, message: 'Minimum 15 minutes' },
                      max: { value: 480, message: 'Maximum 8 hours' }
                    })}
                    error={!!errors.duration}
                    errorText={errors.duration?.message}
                  />
                </div>

                <div>
                  <Input
                    type="textarea"
                    label="Message (optional)"
                    placeholder="Any special requests or notes..."
                    rows={3}
                    {...register('message')}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                  size="large"
                  className="w-full"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {loading ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PublicBooking; 